import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import AnimatedCard from '../../common/AnimatedCard';
import '../../common/Cards.css';
function getRelPos(diff, total) {
  if (total === 4) {
    if (diff === 0) return 'bottom';
    if (diff === 1) return 'right';
    if (diff === 2) return 'top';
    if (diff === 3) return 'left';
  }
  if (total === 6) {
    if (diff === 0) return 'bottom';
    if (diff === 1) return 'bottom-right';
    if (diff === 2) return 'top-right';
    if (diff === 3) return 'top';
    if (diff === 4) return 'top-left';
    if (diff === 5) return 'bottom-left';
  }
  return 'hidden';
}

function getStartCoords(seat) {
  const dist = 500; 
  switch(seat) {
    case 'bottom': return { x: 0, y: dist };
    case 'top':    return { x: 0, y: -dist };
    case 'left':   return { x: -dist, y: 0 };
    case 'right':  return { x: dist, y: 0 };
    case 'top-left':     return { x: -dist, y: -dist/2 };
    case 'top-right':    return { x: dist, y: -dist/2 };
    case 'bottom-left':  return { x: -dist, y: dist/2 };
    case 'bottom-right': return { x: dist, y: dist/2 };
    default: return { x: 0, y: 0 };
  }
}

/* --- Self-contained logic to always know who won the trick --- */
function calculateWinnerId(trick, trumpSuit, isRevealed) {
  if (!trick || trick.length === 0) return null;
  
  // Standard 28/56 rank hierarchy
  const rankOrder28 = { '7': 0, '8': 1, 'Q': 2, 'K': 3, '10': 4, 'A': 5, '9': 6, 'J': 7 };
  const ledSuit = trick[0].card.suit;
  const activeTrump = isRevealed ? trumpSuit : null;

  let winnerIdx = 0;
  for (let i = 1; i < trick.length; i++) {
    const currentCard = trick[i].card;
    const winningCard = trick[winnerIdx].card;

    const currentIsTrump = currentCard.suit === activeTrump;
    const winningIsTrump = winningCard.suit === activeTrump;

    if (currentIsTrump && !winningIsTrump) {
      winnerIdx = i; // Trump beats non-trump
    } else if (currentIsTrump && winningIsTrump) {
      if (rankOrder28[currentCard.rank] > rankOrder28[winningCard.rank]) winnerIdx = i;
    } else if (!currentIsTrump && !winningIsTrump && currentCard.suit === ledSuit) {
      if (rankOrder28[currentCard.rank] > rankOrder28[winningCard.rank]) winnerIdx = i;
    }
  }
  return trick[winnerIdx].player_id;
}


export default function TrickArea({ gs, myId, totalPlayers }) {
  const [displayTrick, setDisplayTrick] = useState([]);
  const [isClearing, setIsClearing] = useState(false);
  const [exitTarget, setExitTarget] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const current = gs.current_trick || [];
    const last = gs.last_trick || [];

    if (current.length > 0) {
      setDisplayTrick(current);
      setIsClearing(false);
    } else if (current.length === 0 && last.length > 0) {
      
      const winnerId = calculateWinnerId(last, gs.trump, gs.trump_revealed);
      if (winnerId !== null) {
        const diff = (winnerId - myId + totalPlayers) % totalPlayers;
        const seat = getRelPos(diff, totalPlayers);
        setExitTarget(getStartCoords(seat)); 
      }

      setDisplayTrick(last);
      setIsClearing(true);
      const timer = setTimeout(() => {
        setDisplayTrick([]);
        setIsClearing(false);
      }, 3000); 
      return () => clearTimeout(timer);
    } else {
      setDisplayTrick([]);
      setIsClearing(false);
    }
  }, [gs.current_trick, gs.last_trick, gs.trump, gs.trump_revealed, myId, totalPlayers]);

  const getExitAnimation = () => {
    return {
      x: exitTarget.x,
      y: exitTarget.y,
      opacity: 0,
      scale: 0.2, 
      rotate: 0,  
      transition: {
        type: "tween",
        /* THE FIX: Increased from 0.45 to 0.65 for a slightly slower, smoother sweep */
        duration: 0.65,
        ease: "easeIn" 
      }
    };
  };

  const getTargetCoords = (seat, total) => {
    if (total === 4) {
      switch(seat) {
        case 'bottom': return { x: 0, y: 55, rotate: 0 };
        case 'top':    return { x: 0, y: -55, rotate: 0 };
        case 'left':   return { x: -65, y: -5, rotate: -8 };
        case 'right':  return { x: 65, y: -5, rotate: 8 };
        default:       return { x: 0, y: 0, rotate: 0 };
      }
    } else {
      switch(seat) {
        case 'bottom':       return { x: 0, y: 95, rotate: 0 };
        case 'top':          return { x: 0, y: -95, rotate: 0 };
        case 'top-left':     return { x: -90, y: -50, rotate: -25 };
        case 'top-right':    return { x: 90, y: -50, rotate: 25 };
        case 'bottom-left':  return { x: -90, y: 50, rotate: -25 };
        case 'bottom-right': return { x: 90, y: 50, rotate: 25 };
        default:             return { x: 0, y: 0, rotate: 0 };
      }
    }
  };

  return (
    <div className={`trick-area trick-area-${totalPlayers} ${isClearing ? 'trick-clearing' : ''}`}>
      <AnimatePresence>
        {displayTrick.map((tc, idx) => {
          const cardId = `trick-card-${tc.player_id}-${tc.card.suit}-${tc.card.rank}-${idx}`;

          const diff = (tc.player_id - myId + totalPlayers) % totalPlayers;
          const seat = getRelPos(diff, totalPlayers);
          
          const { x, y, rotate } = getTargetCoords(seat, totalPlayers);
          const { x: startX, y: startY } = getStartCoords(seat);

          return (
            <AnimatedCard
              key={cardId}
              card={tc.card}
              className="trick-card-slot" 
              targetX={x}
              targetY={y}
              targetRotate={rotate}
              startX={startX} 
              startY={startY} 
              exit={getExitAnimation()} 
              zIndexProp={idx + 10} 
              customDelay={0} 
            />
          );
        })}
      </AnimatePresence>
    </div>
  );
}